import React, { useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormikContext } from "formik";
import {
    getEmbedUrl,
    getFormSpecificPathForSettingCookie,
    prepareDataForAbandonedGiftSubmission,
} from "@qgiv/donation-form";
import { selectStateForSessionStorage } from "../../../redux/slices/appSettingsSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import {
    selectAbandonedGiftToken,
    selectAcceptedEmailReminders,
    selectEmailForReminders,
    setAcceptEmailRemindersData,
    setHasScheduledEmailReminders,
} from "../../../redux/slices/abandonedGiftSlice";
import { selectSubtotalGift } from "../../../redux/slices/donationDetailsSlice";
import { selectConfig } from "../../../redux/slices/configSettingsSlice";
import { sendPostMessage } from "../../PostMessage/postMessage";
import { selectAllDonationSettings } from "../../../redux/slices/donationSettingsSlice";
import { selectAllEventSettings } from "../../../redux/slices/eventSettingsSlice";
import SubmitAbandonedGiftData from "./SubmitAbandonedGiftData";

const ConnectedSubmitAbandonedGiftData = () => {
    const dispatch = useDispatch();
    const abandonedGiftReduxData = useSelector(selectStateForSessionStorage);
    const formSettings = useSelector(selectAllFormSettings);
    const eventSettings = useSelector(selectAllEventSettings);
    const donationSettings = useSelector(selectAllDonationSettings);
    const recipientEmail = useSelector(selectEmailForReminders);
    const token = useSelector(selectAbandonedGiftToken);
    const acceptedEmailReminders = useSelector(selectAcceptedEmailReminders);
    const amount = useSelector(selectSubtotalGift);
    const config = useSelector(selectConfig);

    // these can contain PII, PCI and need to be sanitized before storing
    const { values } = useFormikContext();

    const { id, template } = formSettings;
    const formId = id;
    const { isDisplayTypeModal, embed, path } = config;
    const embedUrl = getEmbedUrl({ config, eventSettings, template });
    const embedId = embed?.id || 0;

    const abandonedGiftData = prepareDataForAbandonedGiftSubmission({
        abandonedGiftReduxData,
        donationSettings,
        formSettings,
        values,
    });

    const formSpecificPath = getFormSpecificPathForSettingCookie({
        embedId,
        eventSettings,
        formSettings,
        path,
    });

    const dispatchSetAcceptEmailReminderDataFalse = useCallback(() => {
        dispatch(
            setAcceptEmailRemindersData({ acceptedEmailReminders: false }),
        );
    }, [dispatch]);

    const dispatchSetHasScheduledReminderEmails = useCallback(() => {
        dispatch(setHasScheduledEmailReminders(true));
    }, [dispatch]);

    const sendEmailsScheduledPostMessage = useCallback(() => {
        const data = { formSpecificPath };
        sendPostMessage("emailsScheduled", data);
    }, [formSpecificPath]);

    const closeModalDisplayType = useCallback(() => {
        if (isDisplayTypeModal) {
            sendPostMessage("closeFullScreenModal");
        }
    }, [isDisplayTypeModal]);

    const emailData = useMemo(
        () => ({
            amount,
            embedUrl,
            formId,
            recipientEmail,
        }),
        [amount, embedUrl, formId, recipientEmail],
    );

    return (
        <SubmitAbandonedGiftData
            abandonedGiftData={abandonedGiftData}
            acceptedEmailReminders={acceptedEmailReminders}
            closeModalDisplayType={closeModalDisplayType}
            dispatchSetAcceptEmailReminderDataFalse={
                dispatchSetAcceptEmailReminderDataFalse
            }
            dispatchSetHasScheduledReminderEmails={
                dispatchSetHasScheduledReminderEmails
            }
            emailData={emailData}
            embedUrl={embedUrl}
            formId={formId}
            recipientEmail={recipientEmail}
            sendEmailsScheduledPostMessage={sendEmailsScheduledPostMessage}
            token={token}
        />
    );
};

export default ConnectedSubmitAbandonedGiftData;
